import { GiftCardOccasionType } from "@/__generated__/graphql";
import CongratsIcon from "@/assets/images/3d-icons/baloon.png";
import BirthdayIcon from "@/assets/images/3d-icons/cake.png";
import ThankYouIcon from "@/assets/images/3d-icons/envelope-purple.png";
import DefaultGiftCardIcon from "@/assets/images/3d-icons/envelope.png";
import ChristmasIcon from "@/assets/images/3d-icons/gift-christmas.png";
import CnyIcon from "@/assets/images/3d-icons/gift-cny.png";
import DeepavaliIcon from "@/assets/images/3d-icons/gift-deepavali.png";
import EidMubarakIcon from "@/assets/images/3d-icons/gift-eid-mubarak.png";
import GiftForYouIcon from "@/assets/images/3d-icons/gift-envelope.png";
import FathersDayIcon from "@/assets/images/3d-icons/gift-fathers-day.png";
import SpecialIcon from "@/assets/images/3d-icons/gift-green.png";
import HalloweenIcon from "@/assets/images/3d-icons/gift-halloween.png";
import MothersDayIcon from "@/assets/images/3d-icons/gift-mothers-day.png";
import NewYearIcon from "@/assets/images/3d-icons/gift-new-year.png";
import ValentinesIcon from "@/assets/images/3d-icons/gift-valentines.png";
import GetWellIcon from "@/assets/images/3d-icons/med.png";
import NewbornIcon from "@/assets/images/3d-icons/newborn.png";
import WeddingIcon from "@/assets/images/3d-icons/rings.png";
import BirthdayHero from "@/assets/images/gift-card/birthday.png";
import ChristmasHero from "@/assets/images/gift-card/christmas.png";
import CnyHero from "@/assets/images/gift-card/cny.png";
import CongratsHero from "@/assets/images/gift-card/congrats.png";
import DeepavaliHero from "@/assets/images/gift-card/deepavali.png";
import EidMubarakHero from "@/assets/images/gift-card/eid-mubarak.png";
import FathersDayHero from "@/assets/images/gift-card/fathers-day.png";
import GetWellHero from "@/assets/images/gift-card/get-well.png";
import GiftForYouHero from "@/assets/images/gift-card/gift-for-you.png";
import HalloweenHero from "@/assets/images/gift-card/halloween.png";
import MothersDayHero from "@/assets/images/gift-card/mothers-day.png";
import NewBornHero from "@/assets/images/gift-card/new-born.png";
import NewYearHero from "@/assets/images/gift-card/new-year.png";
import SpecialHero from "@/assets/images/gift-card/special.png";
import ThankYouHero from "@/assets/images/gift-card/thank-you.png";
import ValentinesHero from "@/assets/images/gift-card/valentines.png";
import WeddingHero from "@/assets/images/gift-card/wedding.png";
import type { BadgeVariant } from "@/components/ui/badge";

import type { GiftCardHistoryItem, GiftCardHistoryTab, GiftCardOccasionKey } from "./types";
import { GiftCardStatus } from "./types";

type GiftCardOccasionAsset = {
  icon: string;
  hero: string;
};

export type GiftCardGroup = "moment" | "festive";

export interface GiftCardOccasionDef {
  key: GiftCardOccasionKey;
  title: string;
  group: GiftCardGroup;
  icon: string;
  hero: string;
  occasionType: GiftCardOccasionType;
}

/**
 * Note: `gift-for-you` and `special` both map to the backend `GENERAL`
 * occasion type. `gift-for-you` is the canonical key used when rendering a
 * received `GENERAL` card (see OCCASION_TYPE_MAP below, which is first-wins).
 */
export const GIFT_CARD_OCCASIONS: GiftCardOccasionDef[] = [
  // Moments (existing)
  {
    key: "gift-for-you",
    title: "Gift for You",
    group: "moment",
    icon: GiftForYouIcon,
    hero: GiftForYouHero,
    occasionType: GiftCardOccasionType.General,
  },
  {
    key: "thank-you",
    title: "Thank you",
    group: "moment",
    icon: ThankYouIcon,
    hero: ThankYouHero,
    occasionType: GiftCardOccasionType.ThankYou,
  },
  {
    key: "special",
    title: "Special",
    group: "moment",
    icon: SpecialIcon,
    hero: SpecialHero,
    occasionType: GiftCardOccasionType.General,
  },
  {
    key: "birthday",
    title: "Birthday",
    group: "moment",
    icon: BirthdayIcon,
    hero: BirthdayHero,
    occasionType: GiftCardOccasionType.Birthday,
  },
  {
    key: "newborn",
    title: "Newborn",
    group: "moment",
    icon: NewbornIcon,
    hero: NewBornHero,
    occasionType: GiftCardOccasionType.Newborn,
  },
  {
    key: "congrats",
    title: "Congrats",
    group: "moment",
    icon: CongratsIcon,
    hero: CongratsHero,
    occasionType: GiftCardOccasionType.Congrats,
  },
  {
    key: "wedding",
    title: "Wedding",
    group: "moment",
    icon: WeddingIcon,
    hero: WeddingHero,
    occasionType: GiftCardOccasionType.Wedding,
  },
  {
    key: "get-well",
    title: "Get Well",
    group: "moment",
    icon: GetWellIcon,
    hero: GetWellHero,
    occasionType: GiftCardOccasionType.GetWell,
  },
  // Festive season
  {
    key: "cny",
    title: "CNY",
    group: "festive",
    icon: CnyIcon,
    hero: CnyHero,
    occasionType: GiftCardOccasionType.ChineseNewYear,
  },
  {
    key: "deepavali",
    title: "Deepavali",
    group: "festive",
    icon: DeepavaliIcon,
    hero: DeepavaliHero,
    occasionType: GiftCardOccasionType.Deepavali,
  },
  {
    key: "valentines",
    title: "Valentine's Day",
    group: "festive",
    icon: ValentinesIcon,
    hero: ValentinesHero,
    occasionType: GiftCardOccasionType.Valentines,
  },
  {
    key: "mothers-day",
    title: "Mother's Day",
    group: "festive",
    icon: MothersDayIcon,
    hero: MothersDayHero,
    occasionType: GiftCardOccasionType.MothersDay,
  },
  {
    key: "fathers-day",
    title: "Father's Day",
    group: "festive",
    icon: FathersDayIcon,
    hero: FathersDayHero,
    occasionType: GiftCardOccasionType.FathersDay,
  },
  {
    key: "halloween",
    title: "Halloween",
    group: "festive",
    icon: HalloweenIcon,
    hero: HalloweenHero,
    occasionType: GiftCardOccasionType.Halloween,
  },
  {
    key: "new-year",
    title: "New Year",
    group: "festive",
    icon: NewYearIcon,
    hero: NewYearHero,
    occasionType: GiftCardOccasionType.NewYear,
  },
  {
    key: "eid-mubarak",
    title: "Eid Mubarak",
    group: "festive",
    icon: EidMubarakIcon,
    hero: EidMubarakHero,
    occasionType: GiftCardOccasionType.EidMubarak,
  },
  {
    key: "christmas",
    title: "Christmas",
    group: "festive",
    icon: ChristmasIcon,
    hero: ChristmasHero,
    occasionType: GiftCardOccasionType.Christmas,
  },
];

export const getOccasionsByGroup = (group: GiftCardGroup) =>
  GIFT_CARD_OCCASIONS.filter((occasion) => occasion.group === group);

export const getGiftCardOccasionGroup = (key: string): GiftCardGroup =>
  GIFT_CARD_OCCASIONS.find((occasion) => occasion.key === key)?.group ?? "moment";

const TAB_ITEMS = [
  { label: "Sent", value: "sent" },
  { label: "Received", value: "received" },
] as const;

export const GIFT_CARD_STATUS_CONFIG: Record<
  GiftCardStatus,
  { label: string; variant: BadgeVariant }
> = {
  [GiftCardStatus.PendingPayment]: {
    label: "Pending Payment",
    variant: "warning",
  },
  [GiftCardStatus.Active]: { label: "Active", variant: "info" },
  [GiftCardStatus.Redeemed]: { label: "Redeemed", variant: "success" },
  [GiftCardStatus.Expired]: { label: "Expired", variant: "danger" },
  [GiftCardStatus.Cancelled]: { label: "Cancelled", variant: "neutral" },
};

const OCCASION_ASSETS_BY_KEY: Partial<Record<GiftCardOccasionKey, GiftCardOccasionAsset>> =
  Object.fromEntries(
    GIFT_CARD_OCCASIONS.map((occasion) => [
      occasion.key,
      { icon: occasion.icon, hero: occasion.hero },
    ]),
  );

const OCCASION_TYPE_MAP = GIFT_CARD_OCCASIONS.reduce(
  (acc, occasion) => {
    if (!acc[occasion.occasionType]) {
      acc[occasion.occasionType] = { key: occasion.key, title: occasion.title };
    }
    return acc;
  },
  {} as Record<GiftCardOccasionType, { key: GiftCardOccasionKey; title: string }>,
);

type GiftCardFragment = {
  id: string;
  code: string;
  amount: number;
  createdAt: string;
  expirationDate?: string | null;
  invoice?: { id: string } | null;
  messageTitle: string;
  messageBody: string;
  occasionType: GiftCardOccasionType;
  senderName: string;
  status: GiftCardStatus;
  sender: { id: string };
  recipient?: { id: string; name: string } | null;
};

/**
 * Maps a raw GiftCard from the GraphQL response to a GiftCardHistoryItem for UI.
 *
 * The backend returns gift cards where the client is sender OR recipient.
 * Tab (sent/received) is determined by comparing sender.id with the current clientId:
 *   - sender.id === clientId → "sent" (name shows recipient)
 *   - sender.id !== clientId → "received" (name shows sender)
 */
export function mapToGiftCardHistoryItem(
  giftCard: GiftCardFragment,
  clientId: string,
): GiftCardHistoryItem {
  const isSent = giftCard.sender.id === clientId;
  return {
    id: giftCard.id,
    code: giftCard.code,
    invoiceId: giftCard.invoice?.id ?? undefined,
    occasion: OCCASION_TYPE_MAP[giftCard.occasionType],
    amount: giftCard.amount,
    recipientName: giftCard.recipient?.name ?? "",
    senderName: giftCard.senderName,
    createdAt: giftCard.createdAt,
    status: giftCard.status,
    isSent,
    messageTitle: giftCard.messageTitle,
    messageBody: giftCard.messageBody,
    expiresAt: giftCard.expirationDate ?? undefined,
  };
}

export const normalizeGiftCardTab = (tab?: string | null): GiftCardHistoryTab | undefined => {
  if (!tab) return undefined;
  return TAB_ITEMS.some((item) => item.value === tab) ? (tab as GiftCardHistoryTab) : undefined;
};

export const buildGiftCardHistoryTabs = (basePath: string) =>
  TAB_ITEMS.map((item) => ({
    label: item.label,
    href: `${basePath}?tab=${item.value}`,
  }));

export const getGiftCardHistoryActiveHref = (basePath: string, tab?: GiftCardHistoryTab) =>
  `${basePath}?tab=${tab ?? "sent"}`;

export const isGiftCardOccasionKey = (key: string): key is GiftCardOccasionKey =>
  key in OCCASION_ASSETS_BY_KEY;

export const getGiftCardOccasionIcon = (key: GiftCardOccasionKey) =>
  OCCASION_ASSETS_BY_KEY[key]?.icon ?? DefaultGiftCardIcon;

export const getGiftCardOccasionHeroImage = (key: GiftCardOccasionKey) =>
  OCCASION_ASSETS_BY_KEY[key]?.hero ?? GiftForYouHero;

/**
 * Gift-card specific name masking for history subtitles.
 * Unlike the generic helper, this also masks single-word names.
 */
export const formatGiftCardClientNameHide = (name: string): string => {
  return name
    .split("\n")
    .map((line) => {
      const words = line.trim().split(" ").filter(Boolean);
      if (!words.length) return "";
      if (words.length === 1) {
        return `${words[0][0]}***`;
      }
      return `${words[0]} ${words[1][0]}***`;
    })
    .join("\n");
};
